import React from 'react';
import { Badge, Button, IconButton } from '@material-ui/core';
import { format } from 'date-fns';
import images from 'assets/images';
import Scrollbars from 'react-custom-scrollbars';
import { useDispatch, useSelector } from 'react-redux';
import { IApplicationState } from 'store/state/app-state';
import { INotificationModel } from 'models/notification';
import notificationService from 'services/notification/notification-service';
import { updateHeaderNotificationsCount } from 'store/actions/general-actions';
import { HttpStatusCode } from 'shared/enums/http-status-code';
import { toast } from 'react-toastify';
import messages from 'shared/constants/messages';
import { DateToLocalString, DateUTCToLocalString } from 'utils/dateFormat';
import DialogForm from 'shared/components/DialogForm';
import { Routing } from 'shared/constants/routing';
import { IBaseSearchRequest, IColumn } from 'models/base-type';
import { Digits } from 'shared/enums/digits';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';

const NOTIFICATION_POLL_INTERVAL = 60000;

const Notification: React.FC = () => {
    const userData = useSelector((state: IApplicationState) => state.UserData);
    const generalData = useSelector((state: IApplicationState) => state?.GeneralData);
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();

    const isMounted = React.useRef(false);
    const isPanelOpen = React.useRef(false);
    const notificationRef = React.useRef<INotificationModel>(null);

    const isNotificationPage = location.pathname.split('/').pop() === 'notification';

    // Unread count — drives the badge; fetched on a polling interval
    const [unreadCount, setUnreadCount] = React.useState<number>(0);

    // Full list — fetched only when the panel is opened
    const [notifications, setNotifications] = React.useState<INotificationModel[]>([]);
    const [isListLoading, setIsListLoading] = React.useState(false);

    const [openDialog, setOpenDialog] = React.useState(false);
    const handleDialogOpen = () => setOpenDialog(true);
    const handleDialogClose = () => setOpenDialog(false);

    const columns: Array<IColumn> = React.useMemo(
        () => [
            { name: 'Title', data: 'title' },
            { name: 'Description', data: 'Description' },
        ],
        [],
    );

    // ---------------------------------------------------------------------------
    // Helpers
    // ---------------------------------------------------------------------------
    const DateUTCToLocalStringFormat = (date: string | Date) => {
        if (!date) return '';
        const parsedDate = typeof date === 'string' ? new Date(date.endsWith('Z') ? date : `${date}Z`) : date;
        return format(parsedDate, 'MM/dd/yyyy');
    };

    /** Check incoming records for a pending video-call alert and open the dialog. */
    const checkVideoCallAlert = React.useCallback((records: INotificationModel[]) => {
        records.forEach((notification) => {
            if (
                notification.refId === 3 &&
                !notification.isAlert &&
                DateUTCToLocalStringFormat(new Date()) === DateUTCToLocalString(notification.createdDate) &&
                !window.location.pathname.includes(Routing.VideoSession)
            ) {
                notificationRef.current = notification;
                handleDialogOpen();
            }
        });
    }, []);

    // ---------------------------------------------------------------------------
    // 1. COUNT — lightweight poll; never fetches the full list
    // ---------------------------------------------------------------------------
    const fetchUnreadCount = React.useCallback(async () => {
        if (!userData?.userIdEnc) return;

        try {
            const response = await notificationService.getNotificationUnreadCount(
                userData.userIdEnc,
                false, // patient portal is never managed-care
            );
            const { data, statusCode } = response?.data ?? {};
            if (statusCode === HttpStatusCode.Success && isMounted.current) {
                setUnreadCount(data ?? 0);
            }
        } catch {
            // Silently ignore — badge retains last known value
        }
    }, [userData?.userIdEnc]);

    // ---------------------------------------------------------------------------
    // 2. LIST — fetched only when the panel opens (or after a mutation)
    // ---------------------------------------------------------------------------
    const buildSearchBody = React.useCallback(
        (): IBaseSearchRequest => ({
            customSearch: JSON.stringify({
                UserId: userData?.userIdEnc,
                IsFromMenu: true,
            }),
            start: Digits.Zero,
            length: Digits.MinusOne,
            columns,
        }),
        [userData?.userIdEnc, columns],
    );

    const fetchNotificationList = React.useCallback(async () => {
        if (!userData?.userIdEnc) return;

        setIsListLoading(true);
        try {
            const response = await notificationService.getNotificationList(buildSearchBody(), true);
            const { data, statusCode } = response?.data ?? {};
            if (statusCode === HttpStatusCode.Success && isMounted.current) {
                const records: INotificationModel[] = data?.records ?? [];
                setNotifications(records);
                // Keep badge in sync after any mutation that triggers a list re-fetch
                setUnreadCount(records.filter((n) => !n.isRead).length);
                // Video-call alert check runs whenever the list is refreshed
                checkVideoCallAlert(records);
            }
        } catch {
            toast.error(messages.SomethingWentWrong);
        } finally {
            if (isMounted.current) setIsListLoading(false);
        }
    }, [buildSearchBody, checkVideoCallAlert, userData?.userIdEnc]);

    // ---------------------------------------------------------------------------
    // Mount / unmount + polling (count only)
    // ---------------------------------------------------------------------------
    React.useEffect(() => {
        isMounted.current = true;

        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, NOTIFICATION_POLL_INTERVAL);

        return () => {
            isMounted.current = false;
            clearInterval(interval);
        };
    }, [fetchUnreadCount, generalData?.isReadNotification]);

    // ---------------------------------------------------------------------------
    // Panel visibility helpers
    // ---------------------------------------------------------------------------
    const showNotification = React.useCallback(() => {
        const willOpen = !document.body.classList.contains('show-notification-popup');
        document.body.classList.toggle('show-notification-popup');

        if (willOpen) {
            isPanelOpen.current = true;
            fetchNotificationList();
        } else {
            isPanelOpen.current = false;
        }
    }, [fetchNotificationList]);

    const hideNotification = React.useCallback(() => {
        document.body.classList.remove('show-notification-popup');
        isPanelOpen.current = false;
    }, []);

    // ---------------------------------------------------------------------------
    // Mutation handlers
    // ---------------------------------------------------------------------------
    const handleDeleteNotification = async (notification: INotificationModel) => {
        try {
            const response = await notificationService.updateNotification({
                ...notification,
                isDeleted: true,
            });
            if (response?.data?.statusCode === HttpStatusCode.Success) {
                if (isNotificationPage) {
                    dispatch(updateHeaderNotificationsCount(!generalData?.isReadNotification));
                } else {
                    await fetchNotificationList();
                }
            }
        } catch {
            toast.error(messages.SomethingWentWrong);
        }
    };

    const handleReadNotification = async (notification: INotificationModel) => {
        try {
            const response = await notificationService.updateNotification({
                ...notification,
                isRead: true,
            });
            if (response?.data?.statusCode === HttpStatusCode.Success) {
                if (isNotificationPage) {
                    dispatch(updateHeaderNotificationsCount(!generalData?.isReadNotification));
                } else {
                    await fetchNotificationList();
                }
            }
        } catch {
            toast.error(messages.SomethingWentWrong);
        }
    };

    const handleAlertNotification = async (notification: INotificationModel) => {
        try {
            const response = await notificationService.updateNotification({
                ...notification,
                isAlert: true,
            });
            if (response?.data?.statusCode === HttpStatusCode.Success) {
                await fetchNotificationList();
            }
        } catch {
            toast.error(messages.SomethingWentWrong);
        } finally {
            handleDialogClose();
        }
    };

    const handleJoinCall = async (notification: INotificationModel) => {
        handleAlertNotification({ ...notification, isRead: true });
        window.open(notification.redirectUrl, '_blank');
    };

    const handleViewAllClick = () => {
        history.push(Routing.Notification);
        hideNotification();
    };

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------
    return (
        <React.Fragment>
            <IconButton component="a" onClick={showNotification}>
                <Badge badgeContent={unreadCount} color="primary" className="danger header-icon">
                    <svg width="21" height="24" viewBox="0 0 21 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M20.4273 15.0727L18.3125 12.9578V10.1562C18.3101 8.22019 17.5899 6.35379 16.2911 4.91795C14.9924 3.48212 13.2074 2.57885 11.2812 2.38281V0.78125H9.71875V2.38281C7.79264 2.57885 6.00763 3.48212 4.70888 4.91795C3.41014 6.35379 2.68992 8.22019 2.6875 10.1562V12.9578L0.572656 15.0727C0.426133 15.2191 0.343794 15.4178 0.34375 15.625V17.9687C0.34375 18.1759 0.42606 18.3747 0.572573 18.5212C0.719086 18.6677 0.9178 18.75 1.125 18.75H6.59375V19.357C6.57676 20.3482 6.9261 21.3107 7.57483 22.0602C8.22356 22.8097 9.12606 23.2934 10.1094 23.4187C10.6525 23.4726 11.2009 23.4122 11.7192 23.2414C12.2376 23.0706 12.7144 22.7932 13.1192 22.4271C13.5239 22.0609 13.8475 21.6141 14.0691 21.1154C14.2908 20.6166 14.4056 20.077 14.4062 19.5312V18.75H19.875C20.0822 18.75 20.2809 18.6677 20.4274 18.5212C20.5739 18.3747 20.6562 18.1759 20.6562 17.9687V15.625C20.6562 15.4178 20.5739 15.2191 20.4273 15.0727ZM12.8438 19.5312C12.8438 20.1528 12.5968 20.749 12.1573 21.1885C11.7177 21.6281 11.1216 21.875 10.5 21.875C9.8784 21.875 9.28226 21.6281 8.84272 21.1885C8.40318 20.749 8.15625 20.1528 8.15625 19.5312V18.75H12.8438V19.5312ZM19.0938 17.1875H1.90625V15.9484L4.02109 13.8336C4.16762 13.6871 4.24996 13.4884 4.25 13.2812V10.1562C4.25 8.49865 4.90848 6.90893 6.08058 5.73683C7.25268 4.56473 8.8424 3.90625 10.5 3.90625C12.1576 3.90625 13.7473 4.56473 14.9194 5.73683C16.0915 6.90893 16.75 8.49865 16.75 10.1562V13.2812C16.75 13.4884 16.8324 13.6871 16.9789 13.8336L19.0938 15.9484V17.1875Z"
                            fill="#77787D"
                        />
                    </svg>
                </Badge>
            </IconButton>

            <div className="notifications-block" onMouseLeave={hideNotification}>
                <div className="block-title">
                    <h6 className="p-0">Notifications</h6>
                    <a onClick={handleViewAllClick} title="View All">
                        View All
                    </a>
                </div>
                <ul className="notifications-list">
                    <Scrollbars className="custom-scroll">
                        {isListLoading ? (
                            <></>
                        ) : (
                            notifications.map((notification: INotificationModel) => (
                                <li key={notification.notificationId}>
                                    <div
                                        title={notification?.title}
                                        className={`noti-item hover ${notification?.isRead ? '' : 'read'}`}
                                        onClick={() => handleReadNotification(notification)}
                                    >
                                        <em className="thumb">
                                            <img src={images.notification} alt="Notification" />
                                        </em>
                                        <div className="noti-details">
                                            <div className="notification-div">
                                                <h6
                                                    className="notification-title"
                                                    title={notification?.isRead ? 'Read' : 'Mark as read'}
                                                >
                                                    {notification.title}
                                                </h6>
                                                <IconButton
                                                    className="action-btn"
                                                    title="Delete Notification"
                                                    onClick={() => handleDeleteNotification(notification)}
                                                >
                                                    <img src={images.deleteGray} alt="Delete" />
                                                </IconButton>
                                            </div>
                                            <p>
                                                <a href={notification.redirectUrl}>Click Here</a> to{' '}
                                                {notification.description}
                                            </p>
                                            <span>{DateToLocalString(new Date(notification?.createdDate))}</span>
                                        </div>
                                    </div>
                                </li>
                            ))
                        )}
                    </Scrollbars>
                </ul>
            </div>

            <DialogForm
                openDialog={openDialog}
                handleDialogClose={() => handleAlertNotification(notificationRef?.current)}
                title="Video Consultation has been started"
                bodyContent={
                    <>
                        <div className="form-control-block">
                            <p>{notificationRef?.current?.description}</p>
                        </div>
                        <div className="btns-block">
                            <Button
                                variant="contained"
                                className="btn"
                                disableElevation
                                color="primary"
                                type="submit"
                                onClick={() => handleJoinCall(notificationRef?.current)}
                            >
                                Join
                            </Button>
                            <Button
                                variant="outlined"
                                className="btn"
                                disableElevation
                                color="primary"
                                onClick={() => handleAlertNotification(notificationRef?.current)}
                            >
                                Cancel
                            </Button>
                        </div>
                    </>
                }
                className="detail-dialog"
            />
        </React.Fragment>
    );
};

export default Notification;
